<template>
  <div class="lead">
    <h2 v-if="isFieldVisible('title')" class="lead__title">
      <slot name="title">{{ $t('leadFormTitle') }}</slot>
    </h2>
    <form @submit.prevent="submit" class="lead__form">
      <InputComponent
        v-if="isFieldVisible('contact.name')"
        v-model="data.contact.name"
        :label="labels['contact.name']"
        :placeholder="placeholders['contact.name']"
        required
        :validate="validator.isFieldValid('contact.name')"
        :validation-message="validator.getFieldError('contact.name')"
      />
      <InputComponent
        v-if="isFieldVisible('contact.email')"
        v-model="data.contact.email"
        :label="labels['contact.email']"
        :placeholder="placeholders['contact.email']"
        required
        :validate="validator.isFieldValid('contact.email')"
        :validation-message="validator.getFieldError('contact.email')"
      />
      <InputComponent
        v-if="isFieldVisible('contact.phone')"
        v-model="data.contact.phone"
        :label="labels['contact.phone']"
        :placeholder="placeholders['contact.phone']"
        required
        :validate="validator.isFieldValid('contact.phone')"
        :validation-message="validator.getFieldError('contact.phone')"
      />
      <InputComponent
        v-if="isFieldVisible('comment')"
        :label="labels.comment"
        :validate="validator.isFieldValid('comment')"
        :validation-message="validator.getFieldError('comment')"
      >
        <textarea
          v-model="data.comment"
          :placeholder="placeholders['comment']"
          class="elder-input__element"
          style="resize: vertical"
        ></textarea>
      </InputComponent>

      <CheckboxComponent
        v-if="isFieldVisible('contact.upsell') && !hideUpsell"
        v-model="data.contact.upsell"
        class="mt-1"
        :label="labels['contact.upsell']"
      />
      <CheckboxComponent v-model="privacyAccepted" required>
        <i18n path="leadPrivacy" tag="span">
          <a :href="privacyUrlComp" target="_blank">{{ $t('privacy').toLowerCase() }}</a>
        </i18n>
      </CheckboxComponent>
      <slot name="submit-alert" v-if="submitted">
        <AlertComponent class="mt-2 lead__form-alert">
          {{ $t('leadMessageSent') }}
        </AlertComponent>
      </slot>

      <div class="lead__form-actions">
        <ButtonComponent
          :label="actionLabel"
          :success-label="$t('completed')"
          :state-timeout="4000"
          theme="primary"
          type="submit"
          :icon="['far', 'arrow-right']"
          :promise="promise"
        />
        <slot name="actions" />
      </div>

      <slot name="below" />
    </form>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { Clone } from '@/utils'
import { mapi18n } from '@/i18n'

import Validator from './mixins/validator'
import BrowserApiMixin from './mixins/browser-api'
import { InputComponent } from 'vue-elder-input'
import { CheckboxComponent } from 'vue-elder-checkbox'
import { AlertComponent } from 'vue-elder-alert'

import API from './api'

const template = {
  contact: {
    name: null,
    email: null,
    phone: null,
    upsell: false,
  },
  comment: null,
}

export default {
  mixins: [Validator, BrowserApiMixin('lead')],
  props: {
    defaultData: {
      type: Object,
      default: () => ({}),
    },
    actionLabel: {
      type: String,
      default() {
        return this.$t('leadFormTitle')
      },
    },
    project: String,
    reference: [Object, Array],
    fields: {
      type: Array,
      default: () => [],
    },
    placeholders: {
      type: Object,
      default: () => ({
        'contact.name': undefined,
        'contact.email': undefined,
        'contact.phone': undefined,
        comment: undefined,
      }),
    },
    tags: {
      type: Array,
      default: () => [],
    },
    privacyUrl: {
      type: String,
    },
  },
  watch: {
    defaultData: {
      handler: 'reset',
    },
  },
  data() {
    return {
      data: null,
      privacyAccepted: false,
      promise: null,
      submitted: false,
      validator: {
        rules: {
          'contact.name': 'required',
          'contact.email': 'required|email',
          'contact.phone': 'required|phone',
        },
        labels: 'labels',
        data: 'data',
      },
    }
  },
  computed: {
    ...mapState('Root', ['item']),
    ...mapi18n('labels', {
      'contact.name': 'name',
      'contact.email': 'email',
      'contact.phone': 'phone',
      'contact.upsell': 'contactUpsell',
      comment: 'leadMessage',
    }),
    hideUpsell() {
      return KvassConfig.get('siteSettings.hideLeadUpsell')
    },
    references() {
      if (!this.reference) return []
      return this.reference instanceof Array ? this.reference : [this.reference]
    },
    privacyUrlComp() {
      if (this.privacyUrl) return this.privacyUrl
      return this.project
        ? `/api/legal/privacy/project/${this.project}`
        : '/api/legal/privacy/tenant'
    },
  },
  methods: {
    submit() {
      if (!this.validator.passes || !this.privacyAccepted) return
      this.submitted = true

      let data = Clone(this.data)
      if (this.tags.length) data.contact.tags = this.tags

      this.promise = API.createLead({
        ...data,
        project: this.project,
        references: this.references,
      }).then(() => {
        this.$tracker.lead(data)
        let { comment, ...rest } = this.data
        sessionStorage.setItem('lead', JSON.stringify(rest))
        this.$emit('onSubmit')
        this.reset()
      })
    },
    reset() {
      let store = {}
      try {
        store = JSON.parse(sessionStorage.getItem('lead'))
      } catch (e) {}
      this.data = Object.assign(Clone(template), Clone(this.defaultData), store)
    },
    isFieldVisible(key) {
      let inclusions = this.fields.filter(f => !f.startsWith('!'))
      let exclusions = this.fields.filter(f => f.startsWith('!')).map(f => f.substring(1))

      if (inclusions.length) return inclusions.includes(key)
      if (exclusions.length) return !exclusions.includes(key)

      return true
    },
  },
  created() {
    this.reset()
  },
  components: {
    InputComponent,
    CheckboxComponent,
    AlertComponent,
  },
}
</script>

<style lang="scss">
.lead {
  position: relative;
  display: flex;
  flex-direction: column;

  &__title {
    margin-bottom: 1.5rem;
  }

  &__form {
    background-color: white;
    border-radius: $border-radius;

    display: grid;
    grid-gap: 1rem;

    flex-grow: 1;

    &-actions {
      flex-wrap: wrap;
      display: flex;
      justify-content: center;
      margin-top: 1.5rem;
      gap: 4rem;

      @media (max-width: 767px) {
        gap: 1.5rem;
      }
    }
    &-alert {
      background-color: css-lighten('primary', 60%);
    }
  }
}
</style>
